import React, { useEffect } from 'react';

const AWVI = () => {
  // SEO: Set document title and meta tags
  useEffect(() => {
    // Set page title
    document.title = 'Africa Wellness Voices Initiative (AWVI) - Mental Health Advocacy Movement | SereniMind';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        'Africa Wellness Voices Initiative (AWVI) - A youth-led digital mental health advocacy movement coordinated by SereniMind. Reached 100M+ people across 20+ African countries. View our impact compendium and media coverage.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Africa Wellness Voices Initiative (AWVI) - A youth-led digital mental health advocacy movement coordinated by SereniMind. Reached 100M+ people across 20+ African countries. View our impact compendium and media coverage.';
      document.head.appendChild(meta);
    }

    // Set keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = 'Africa Wellness Voices Initiative, AWVI, mental health Africa, youth mental health, SereniMind, mental health advocacy, African youth, digital health campaign, UN Youth Office, mental wellbeing Africa';

    // Open Graph tags for social media
    const setOGTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    setOGTag('og:title', 'Africa Wellness Voices Initiative - Mental Health Movement');
    setOGTag('og:description', 'Youth-led digital advocacy movement reaching 100M+ people across Africa. Coordinated by SereniMind.');
    setOGTag('og:type', 'website');
    setOGTag('og:url', 'https://www.serenimind.com.ng/awvi');
    setOGTag('og:site_name', 'SereniMind');
    
    // Twitter Card tags
    const setTwitterTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    setTwitterTag('twitter:card', 'summary_large_image');
    setTwitterTag('twitter:title', 'Africa Wellness Voices Initiative - Mental Health Movement');
    setTwitterTag('twitter:description', 'Youth-led digital advocacy movement reaching 100M+ people across Africa.');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://www.serenimind.com.ng/awvi');

    // Cleanup function
    return () => {
      document.title = 'SereniMind - Mental Health & Wellness Platform';
    };
  }, []);

  return (
    <>
      {/* Structured Data - JSON-LD for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Africa Wellness Voices Initiative",
          "alternateName": "AWVI",
          "url": "https://www.serenimind.com.ng/awvi",
          "description": "Youth-led digital advocacy movement for mental health and wellbeing across Africa",
          "founder": {
            "@type": "Person",
            "name": "Ridwan Oyenuga",
            "jobTitle": "Founder & Coordinator"
          },
          "parentOrganization": {
            "@type": "Organization",
            "name": "SereniMind",
            "url": "https://www.serenimind.com.ng"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "ridwanoyenuga@serenimind.com.ng",
            "contactType": "Media Relations"
          },
          "sameAs": [
            "https://www.msn.com/en-xl/africa/top-stories/stigma-economic-pressure-fuel-silent-mental-health-struggles-among-young-africans-expert/ar-AA1W7rPc",
            "https://globalsouthworld.com/article/a-youth-led-digital-movement-is-reshaping-how-africa-talks-about-mental-health",
            "https://allafrica.com/stories/202602110131.html"
          ]
        })}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          "name": "Africa Wellness Voices Initiative",
          "description": "Continental mental health advocacy campaign reaching 100M+ people across 20+ African countries",
          "startDate": "2026-02",
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
          "location": {
            "@type": "VirtualLocation",
            "url": "https://www.serenimind.com.ng/awvi"
          },
          "organizer": {
            "@type": "Organization",
            "name": "SereniMind",
            "url": "https://www.serenimind.com.ng"
          },
          "performer": {
            "@type": "Person",
            "name": "Ridwan Oyenuga"
          }
        })}
      </script>

      <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white mt-[95px]">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-[#2d4f3f] via-[#31452C] to-[#2d4739] text-white" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
                AFRICA WELLNESS VOICES INITIATIVE
              </h1>
              <p className="text-2xl md:text-3xl mb-4 text-[#98f3c3]" role="doc-subtitle">Impact Compendium</p>
              <p className="text-xl md:text-2xl mb-8 text-[#98f3c3]/80">February 2026 Edition</p>
              <div className="flex flex-wrap justify-center gap-4 text-lg" role="list" aria-label="Initiative focus areas">
                <span className="bg-white/20 px-6 py-2 rounded-full backdrop-blur-sm" role="listitem">Youth Voices</span>
                <span className="bg-white/20 px-6 py-2 rounded-full backdrop-blur-sm" role="listitem">Digital Advocacy</span>
                <span className="bg-white/20 px-6 py-2 rounded-full backdrop-blur-sm" role="listitem">Continental Impact</span>
              </div>
              <p className="mt-8 text-xl text-[#98f3c3]">Coordinated by SereniMind</p>
            </div>
          </div>
        </header>

        {/* Executive Summary */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="executive-summary">
          <div className="bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 id="executive-summary" className="text-4xl font-bold text-gray-900 mb-6">Executive Summary</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p className="text-xl">
              In February 2026, a youth-led digital advocacy movement sparked a continent-wide conversation 
              on mental health, resilience, and wellbeing.
            </p>
            <p>
              The Africa Wellness Voices Initiative (AWVI), coordinated by SereniMind, amplified youth voices 
              across Africa through daily digital spotlights that encouraged open dialogue and reduced stigma 
              around mental health.
            </p>
            <p>
              Within weeks of launch, the initiative reached over <strong className="text-[#2d4f3f]">100 million people</strong>, 
              featured voices from more than <strong className="text-[#2d4f3f]">20 countries</strong>, and gained coverage 
              across major African and international media platforms.
            </p>
            <p>
              The initiative was also circulated through youth engagement networks of the <strong className="text-[#2d4f3f]">United Nations Youth Office</strong>, 
              expanding visibility and participation across global youth communities.
            </p>
            <p className="text-xl font-semibold text-[#2d4f3f]">
              What began as a rapid response to growing mental health pressures has evolved into one of the 
              fastest-growing youth-led mental health advocacy movements emerging from Africa.
            </p>
          </div>
          </div>
        </section>

        {/* Impact Snapshot - Statistics Grid */}
        <section className="bg-gradient-to-r from-[#2d4f3f] to-[#31452C] py-16" aria-labelledby="impact-snapshot">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="impact-snapshot" className="text-4xl font-bold text-white text-center mb-12">Impact Snapshot</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-[#98f3c3] mb-2">100M+</div>
              <div className="text-xl text-white">Estimated Digital Reach</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-[#98f3c3] mb-2">22+</div>
              <div className="text-xl text-white">Media Platforms & Broadcasts</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-[#ffa07a] mb-2">20+</div>
              <div className="text-xl text-white">Countries Represented</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-[#ffa07a] mb-2">25+</div>
              <div className="text-xl text-white">Organizations & Partners</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-[#98f3c3] mb-2">50+</div>
              <div className="text-xl text-white">Youth Voices Featured</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center transform hover:scale-105 transition-transform">
              <div className="text-5xl font-bold text-[#ffa07a] mb-2">✓</div>
              <div className="text-xl text-white">UN Youth Network Circulation</div>
            </div>
          </div>
          <p className="text-center text-xl text-[#98f3c3] mt-12">
            Recognised among the fastest-growing youth mental health advocacy movements emerging from Africa
          </p>
          </div>
        </section>

        {/* Why This Movement Was Necessary */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="why-necessary">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 id="why-necessary" className="text-4xl font-bold text-gray-900 mb-6">Why This Movement Was Necessary</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p className="text-xl">
              Mental health is increasingly recognised as a major public health and development issue across Africa.
            </p>
            <p>
              Economic pressure, unemployment, conflict, climate stress, migration pressures, and social instability 
              continue to affect wellbeing across the continent. Young people are particularly vulnerable, often 
              navigating emotional distress without structured support systems.
            </p>
            <div className="bg-[#ffa07a]/10 border-l-4 border-[#ffa07a] p-6 my-6">
              <p className="font-semibold text-gray-900 mb-3">Across many African countries:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Mental health services remain limited</li>
                <li>Stigma discourages open conversations</li>
                <li>Professional support is scarce</li>
                <li>Young people rely on informal coping mechanisms</li>
              </ul>
            </div>
            <p>
              Public health experts continue to emphasise the urgent need for awareness, early conversations, 
              and community-based support.
            </p>
            <p className="text-xl font-semibold text-[#2d4f3f]">
              At the same time, Africa's rapidly expanding digital connectivity presents new opportunities for 
              engagement, education, and support. AWVI emerged at the intersection of urgency and possibility.
            </p>
          </div>
          </div>
        </section>

        {/* The Story Behind the Initiative */}
        <section className="bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 py-16" aria-labelledby="story-behind">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 id="story-behind" className="text-4xl font-bold text-gray-900 mb-6 text-center">The Story Behind the Initiative</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4 text-center">
              <p className="text-xl">
                The Africa Wellness Voices Initiative was inspired by lived realities.
              </p>
              <p>
                Across African communities, young people face pressure to succeed amid economic uncertainty, 
                social expectations, and limited support systems. Conversations around mental health often 
                remain hidden due to stigma and cultural silence.
              </p>
              <p className="text-2xl font-bold text-[#2d4f3f]">
                The initiative was created to make these conversations visible.
              </p>
              <p className="text-xl">
                It aimed to amplify voices, encourage openness, and create a shared continental dialogue 
                around wellbeing.
              </p>
            </div>
            </div>
          </div>
          </section>

        {/* How the Initiative Worked */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="how-it-worked">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 id="how-it-worked" className="text-4xl font-bold text-gray-900 mb-6">How the Initiative Worked</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p className="text-xl">
              The Africa Wellness Voices Initiative is a fully digital advocacy movement.
            </p>
            <p>
              Each day throughout February, youth leaders, advocates, organizations, and community voices 
              from different African countries shared short statements promoting mental wellbeing and 
              emotional resilience.
            </p>
            <div className="bg-gradient-to-r from-[#98f3c3]/30 to-[#98f3c3]/20 rounded-2xl p-6 my-6">
              <p className="font-semibold text-gray-900 mb-3 text-lg">The campaign encouraged:</p>
              <ul className="grid md:grid-cols-2 gap-3">
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2">✓</span>
                  <span>Open conversations about mental health</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2">✓</span>
                  <span>Stigma reduction</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2">✓</span>
                  <span>Collective responsibility for wellbeing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2">✓</span>
                  <span>Youth participation across borders</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2">✓</span>
                  <span>Safe digital dialogue</span>
                </li>
              </ul>
            </div>
            <p>
              Participation was open to individuals, youth groups, institutions, and community organizations.
            </p>
          </div>
          </div>
        </section>

        {/* Media & Global Recognition */}
        <section className="bg-gray-900 text-white py-16" aria-labelledby="media-recognition">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="media-recognition" className="text-4xl font-bold text-center mb-12">Media & Global Recognition</h2>
          
          {/* Global & International */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 text-[#98f3c3] border-b border-[#98f3c3] pb-2">
              Global & International Platforms
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <a href="https://www.msn.com/en-xl/africa/top-stories/stigma-economic-pressure-fuel-silent-mental-health-struggles-among-young-africans-expert/ar-AA1W7rPc" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold text-lg">MSN Africa</div>
                <div className="text-sm text-gray-300 mt-1">Stigma, economic pressure fuel silent mental health struggles</div>
              </a>
              <a href="https://globalsouthworld.com/article/a-youth-led-digital-movement-is-reshaping-how-africa-talks-about-mental-health" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold text-lg">Global South World (UK)</div>
                <div className="text-sm text-gray-300 mt-1">Youth-led digital movement reshaping Africa's mental health dialogue</div>
              </a>
              <a href="https://allafrica.com/stories/202602110131.html" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold text-lg">AllAfrica (Feature 1)</div>
                <div className="text-sm text-gray-300 mt-1">Pan-African coverage</div>
              </a>
              <a href="https://allafrica.com/stories/202602120590.html" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold text-lg">AllAfrica (Feature 2)</div>
                <div className="text-sm text-gray-300 mt-1">Continental impact report</div>
              </a>
            </div>
          </div>

          {/* Pan-African Platforms */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 text-[#98f3c3] border-b border-[#98f3c3] pb-2">
              Pan-African Platforms
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <a href="https://www.etamagazine.com/how-technology-is-changing-mental-health-support-for-africas-youth/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Eta Magazine</div>
                <div className="text-sm text-gray-300 mt-1">Technology changing mental health support</div>
              </a>
              <a href="https://africamentalhealth.com/my-journey-with-mental-health-began-from-a-deeply-personal-place-ridwan-oyenuga/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">African Mental Health</div>
                <div className="text-sm text-gray-300 mt-1">Founder's personal journey</div>
              </a>
              <a href="https://www.hubzmedia.africa/youth-driven-digital-health-movement-unites-african-voices-on-mental-wellness/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Hubz Media Africa (Kenya)</div>
                <div className="text-sm text-gray-300 mt-1">Youth-driven movement unites voices</div>
              </a>
              <a href="https://leregard.info/serenimind-lance-la-campagne-initiative-africa-wellness-voices-pour-le-bien-etre-mental-des-jeunes-africains/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Le Regard (DR Congo)</div>
                <div className="text-sm text-gray-300 mt-1">French-language coverage</div>
              </a>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4">
                <div className="font-semibold">EKOT Radio 97.9 FM (Zambia)</div>
                <div className="text-sm text-gray-300 mt-1">Regional broadcast discussion</div>
              </div>
            </div>
          </div>

          {/* Nigeria National Coverage */}
          <div>
            <h3 className="text-2xl font-bold mb-6 text-[#ffa07a] border-b border-[#ffa07a] pb-2">
              Nigeria National Coverage
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <a href="https://www.premiumtimesng.com/news/headlines/855687-stigma-economic-pressure-fuel-silent-mental-health-struggles-among-young-africans-expert.html" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Premium Times</div>
              </a>
              <a href="https://guardian.ng/opinion/mental-health-pressure-on-african-youth-drives-rise-in-digital-health-advocacy/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">The Guardian Nigeria</div>
              </a>
              <a href="https://dailytrust.com/firm-launches-africa-wide-digital-push-on-youth-mental-health/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Daily Trust</div>
              </a>
              <a href="https://www.vanguardngr.com/2026/02/serenimind-spurs-digital-innovation-for-african-youth-mental-wellness/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Vanguard Nigeria</div>
              </a>
              <a href="https://businessday.ng/technology/article/nigerian-youth-led-startup-takes-mental-health-campaign-to-25-african-countries/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">BusinessDay</div>
              </a>
              <a href="https://businesspost.ng/health/africa-wellness-voices-initiative-promotes-mental-wellbeing/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Business Post</div>
              </a>
              <a href="https://leadership.ng/serenimind-launches-pan-african-digital-push-on-youth-mental-health/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Leadership</div>
              </a>
              <a href="https://independent.ng/african-youth-drive-digital-movement-amid-escalating-mental-health-burden/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Independent Nigeria</div>
              </a>
              <a href="https://techparley.com/nigerian-healthtech-startup-serenimind-launches-digital-campaign-to-tackle-mental-health-across-25-african-countries/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">TechParley</div>
              </a>
              <a href="https://thenationonlineng.net/mental-health-advocates-turn-to-technology-to-reach-african-youths/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">The Nation</div>
              </a>
              <a href="https://www.thisdaylive.com/2026/02/21/digital-communities-emerge-as-lifelines-for-africas-youth-amid-rising-mental-health-pressures/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">ThisDay Live (Feature 1)</div>
              </a>
              <a href="https://www.thisdaylive.com/2026/02/25/digital-advocacy-expands-across-africa-to-tackle-youth-mental-health-crisis/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">ThisDay Live (Feature 2)</div>
              </a>
              <a href="https://newsexpressngr.com/news/296491/mental-health-strain-deepens-among-african-youth-amid-economic-uncertainty-insecurity" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">News Express Nigeria</div>
              </a>
              <a href="https://ng.headtopics.com/news/stigma-economic-pressure-fuel-silent-mental-health-79578612" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Head Topics Nigeria</div>
              </a>
              <a href="https://nigeriastartupact.ng/serenimind-launches-africa-wellness-voices-initiative/" 
                 target="_blank" rel="noopener noreferrer"
                 className="bg-white/10 hover:bg-white/20 backdrop-blur-lg rounded-xl p-4 transition-all">
                <div className="font-semibold">Nigeria Startup Act</div>
              </a>
            </div>
          </div>
          </div>
        </section>

        {/* UN Youth Network */}
        <section className="bg-gradient-to-r from-[#2d4f3f] to-[#31452C] py-16" aria-labelledby="un-youth-network">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 text-center">
              <div className="text-6xl mb-6" role="img" aria-label="United Nations Flag">🇺🇳</div>
              <h2 id="un-youth-network" className="text-4xl font-bold text-white mb-6">United Nations Youth Network Circulation</h2>
            <p className="text-xl text-[#98f3c3] max-w-4xl mx-auto leading-relaxed">
              The Africa Wellness Voices Initiative was circulated through youth engagement networks of the 
              <span className="font-bold text-white"> United Nations Youth Office</span>, supporting wider participation 
              and strengthening awareness among youth communities globally.
            </p>
          </div>
          </div>
        </section>

        {/* Digital Advocacy & Technology Impact */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="digital-advocacy">
          <div className="bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 id="digital-advocacy" className="text-4xl font-bold text-gray-900 mb-6">Digital Advocacy & Technology Impact</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p className="text-xl">
              The initiative demonstrates the growing role of digital platforms in public health awareness.
            </p>
            <div className="bg-white rounded-2xl p-6 my-6 shadow-md">
              <p className="font-semibold text-gray-900 mb-3 text-lg">Mobile connectivity and social media now enable:</p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2 text-xl">→</span>
                  <span>Cross-border dialogue</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2 text-xl">→</span>
                  <span>Youth-driven storytelling</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2 text-xl">→</span>
                  <span>Stigma reduction through shared experiences</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2 text-xl">→</span>
                  <span>Rapid awareness campaigns</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#2d4f3f] mr-2 text-xl">→</span>
                  <span>Access to support networks</span>
                </li>
              </ul>
            </div>
            <p className="text-xl font-semibold text-[#2d4f3f]">
              Digital advocacy is becoming a critical tool in strengthening community wellbeing across Africa.
            </p>
          </div>
          </div>
        </section>

        {/* What This Means for Africa */}
        <section className="bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 py-16" aria-labelledby="africa-impact">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="africa-impact" className="text-4xl font-bold text-gray-900 mb-8 text-center">What This Means for Africa</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-2xl font-bold text-center text-[#2d4f3f] mb-8">
              Mental health is central to Africa's development future.
            </p>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <p className="font-semibold text-gray-900 mb-4 text-lg">Addressing wellbeing strengthens:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 p-4 rounded-lg">
                  <span className="text-2xl mr-3">💪</span>
                  <span>Youth productivity and resilience</span>
                </div>
                <div className="flex items-center bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 p-4 rounded-lg">
                  <span className="text-2xl mr-3">🤝</span>
                  <span>Social cohesion</span>
                </div>
                <div className="flex items-center bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 p-4 rounded-lg">
                  <span className="text-2xl mr-3">🏘️</span>
                  <span>Community stability</span>
                </div>
                <div className="flex items-center bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 p-4 rounded-lg">
                  <span className="text-2xl mr-3">📚</span>
                  <span>Educational outcomes</span>
                </div>
                <div className="flex items-center bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 p-4 rounded-lg">
                  <span className="text-2xl mr-3">💼</span>
                  <span>Economic participation</span>
                </div>
              </div>
              <p className="mt-6 text-gray-700">
                Public health advocates emphasise that prevention, awareness, and early conversations are 
                essential to long-term wellbeing outcomes.
              </p>
            </div>
          </div>
          </div>
        </section>

        {/* The Way Forward */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="way-forward">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 id="way-forward" className="text-4xl font-bold text-gray-900 mb-6">The Way Forward</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-lg mb-4">Stakeholders highlight several priorities:</p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-[#2d4f3f] mr-3 text-2xl">▸</span>
                <span>Integrating mental health into primary healthcare</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#2d4f3f] mr-3 text-2xl">▸</span>
                <span>Strengthening community-based support systems</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#2d4f3f] mr-3 text-2xl">▸</span>
                <span>Leveraging digital health tools for awareness and early support</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#2d4f3f] mr-3 text-2xl">▸</span>
                <span>Expanding youth-focused wellbeing programs</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#2d4f3f] mr-3 text-2xl">▸</span>
                <span>Encouraging cross-country collaboration</span>
              </li>
            </ul>
          </div>
          </div>
        </section>

        {/* What Comes Next */}
        <section className="bg-gradient-to-r from-[#2d4f3f] via-[#31452C] to-[#2d4739] py-16" aria-labelledby="what-comes-next">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="what-comes-next" className="text-4xl font-bold text-white mb-8 text-center">What Comes Next</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white">
              <div className="text-3xl mb-3">🌍</div>
              <h3 className="font-bold text-xl mb-2">Expand Participation</h3>
              <p className="text-[#98f3c3]">Across more African countries</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white">
              <div className="text-3xl mb-3">📣</div>
              <h3 className="font-bold text-xl mb-2">Deepen Engagement</h3>
              <p className="text-[#98f3c3]">Youth engagement and storytelling</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white">
              <div className="text-3xl mb-3">💬</div>
              <h3 className="font-bold text-xl mb-2">Strengthen Spaces</h3>
              <p className="text-[#98f3c3]">Digital safe spaces for wellbeing dialogue</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="font-bold text-xl mb-2">Support Research</h3>
              <p className="text-[#98f3c3]">Research and policy conversations</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white">
              <div className="text-3xl mb-3">🤝</div>
              <h3 className="font-bold text-xl mb-2">Build Partnerships</h3>
              <p className="text-[#98f3c3]">With health, youth, and development institutions</p>
            </div>
          </div>
          </div>
        </section>

        {/* Founder's Note */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-labelledby="founders-note">
          <div className="bg-gradient-to-r from-[#98f3c3]/20 to-[#ffa07a]/10 rounded-3xl p-8 md:p-12 shadow-2xl border-l-8 border-[#2d4f3f]">
            <h2 id="founders-note" className="text-3xl font-bold text-gray-900 mb-6">Founder's Note</h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p className="italic">
              "The Africa Wellness Voices Initiative began as an urgent response to a growing need."
            </p>
            <p>
              "What followed demonstrated the power of collective voices, collaboration, and digital connection."
            </p>
            <p className="font-bold text-xl text-[#2d4f3f]">
              "The conversations started in February are only the beginning."
            </p>
            <p className="text-right text-gray-600 mt-6">
              — Ridwan Oyenuga<br/>
              <span className="text-sm">Founder, SereniMind</span><br/>
              <span className="text-sm">Coordinator, Africa Wellness Voices Initiative</span>
            </p>
          </div>
          </div>
        </section>

        {/* Call for Partners */}
        <section className="bg-gradient-to-r from-[#2d4f3f] to-[#31452C] py-16" aria-labelledby="call-for-partners">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 id="call-for-partners" className="text-4xl font-bold text-white mb-4">Call for Partners & Collaboration</h2>
            <p className="text-xl text-[#98f3c3] max-w-3xl mx-auto">
              Organizations, institutions, and partners are invited to collaborate in strengthening 
              youth mental wellbeing across Africa.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white text-center">
              <div className="text-3xl mb-2">🎯</div>
              <div className="font-semibold">Youth Engagement Programs</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white text-center">
              <div className="text-3xl mb-2">🏥</div>
              <div className="font-semibold">Public Health Awareness</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white text-center">
              <div className="text-3xl mb-2">💻</div>
              <div className="font-semibold">Digital Wellbeing Initiatives</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white text-center">
              <div className="text-3xl mb-2">📋</div>
              <div className="font-semibold">Research and Policy Dialogue</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 text-white text-center">
              <div className="text-3xl mb-2">🌐</div>
              <div className="font-semibold">Cross-Country Partnerships</div>
            </div>
          </div>
          <div className="text-center">
            <a href="mailto:ridwanoyenuga@serenimind.com.ng" 
               className="inline-block bg-white text-[#2d4f3f] font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all text-lg shadow-xl"
               aria-label="Contact us via email">
              Get In Touch
            </a>
          </div>
          </div>
        </section>

        {/* Closing Statement */}
        <footer className="bg-gradient-to-b from-gray-900 to-black text-white py-20" role="contentinfo">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6 text-xl md:text-2xl leading-relaxed">
            <p className="font-light">
              This initiative demonstrates what becomes possible when urgency meets action 
              and voices are amplified across borders.
            </p>
            <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#98f3c3] to-[#ffa07a] bg-clip-text text-transparent">
              It is not a campaign.
            </p>
            <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#ffa07a] to-[#98f3c3] bg-clip-text text-transparent">
              It is a continental conversation.
            </p>
            <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#98f3c3] to-[#2d4f3f] bg-clip-text text-transparent">
              It is a movement.
            </p>
            <p className="text-4xl md:text-5xl font-bold text-white mt-8">
              And it continues.
            </p>
          </div>
          
          <div className="mt-16 pt-8 border-t border-gray-700">
            <p className="text-lg text-gray-400 mb-4">Media Contact</p>
            <p className="text-xl font-semibold">Ridwan Oyenuga</p>
            <p className="text-gray-300">Founder, SereniMind</p>
            <p className="text-gray-300 mb-4">Coordinator, Africa Wellness Voices Initiative</p>
            <a href="https://www.serenimind.com.ng" target="_blank" rel="noopener noreferrer" 
               className="text-[#98f3c3] hover:text-[#ffa07a]">www.serenimind.com.ng</a>
            <br/>
            <a href="mailto:ridwanoyenuga@serenimind.com.ng" 
               className="text-[#98f3c3] hover:text-[#ffa07a]"
               aria-label="Email Ridwan Oyenuga">ridwanoyenuga@serenimind.com.ng</a>
          </div>
        </div>
        </footer>
      </article>
    </>
  );
};

export default AWVI;
